import {
  type ComponentizableCartLine,
  type CartLine,
} from "@shopify/hydrogen-react/storefront-api-types";
import { type PartialDeep } from "type-fest";
import {
  CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS,
  STOREFRONT_CART_ATTRIBUTES,
} from "@tablescape/constants";
import { type CartLineLike, hasCartLineAttribute } from "./cart";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === "object";

export type ProductGwpGiftLink = {
  cartLineId: string | undefined;
  parentVariantId: string | undefined;
};

export const isLegacyProductGwpGiftLink = (link: ProductGwpGiftLink): boolean =>
  !link.cartLineId && !link.parentVariantId;

const readGwpAttributeString = (
  parsed: Record<string, unknown>,
  key: CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS
): string | undefined => {
  const value = parsed[key];
  return typeof value === "string" ? value : undefined;
};

export const isProductGwpParentLineCandidate = (
  line: CartLineLike | null | undefined
): boolean => {
  if (!line?.id) {
    return false;
  }

  if (
    hasCartLineAttribute(line, STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE)
  ) {
    return false;
  }

  const merchandise = line.merchandise;
  if (merchandise == null || typeof merchandise !== "object") {
    return false;
  }

  const giftWithPurchase = Reflect.get(merchandise, "giftWithPurchase");
  if (giftWithPurchase == null || typeof giftWithPurchase !== "object") {
    return false;
  }

  const reference = Reflect.get(giftWithPurchase, "reference");
  return reference != null && typeof reference === "object";
};

export const parseProductGwpGiftLink = (
  attributeValue: string | null | undefined
): ProductGwpGiftLink | undefined => {
  if (!attributeValue) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(attributeValue);
    if (!isRecord(parsed)) {
      return undefined;
    }

    const attributeRecord = parsed;
    const spendingThreshold = readGwpAttributeString(
      attributeRecord,
      CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.SPENDING_THRESHOLD
    );

    if (spendingThreshold) {
      return undefined;
    }

    const cartLineId = readGwpAttributeString(
      attributeRecord,
      CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.CART_LINE_ID
    );
    const parentVariantId = readGwpAttributeString(
      attributeRecord,
      CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.PARENT_VARIANT_ID
    );

    if (!cartLineId && !parentVariantId) {
      // Pre-stack product GWP gifts only stored badge/createdAt/isRemovable.
      // Treat those signals as a legacy product gift link when modern ids are absent.
      const createdAt =
        attributeRecord[
          CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.CREATED_AT
        ];
      const isRemovable =
        attributeRecord[
          CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.IS_REMOVABLE
        ];
      const hasProductGiftSignal =
        readGwpAttributeString(
          attributeRecord,
          CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.GIFT_BADGE
        ) != null ||
        (typeof createdAt === "number" && Number.isFinite(createdAt)) ||
        typeof isRemovable === "boolean";

      if (!hasProductGiftSignal) {
        return undefined;
      }

      return { cartLineId: undefined, parentVariantId: undefined };
    }

    return { cartLineId, parentVariantId };
  } catch {
    return undefined;
  }
};

export const resolveProductGwpActiveParentLineId = (
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  giftLine: CartLineLike | null | undefined
): CartLine["id"] | undefined => {
  if (!giftLine?.id || !isProductGwpGiftLine(giftLine)) {
    return undefined;
  }

  const giftAttribute = giftLine.attributes?.find(
    (attribute) =>
      attribute?.key === STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
  );
  const link = parseProductGwpGiftLink(giftAttribute?.value);

  if (!link) {
    return undefined;
  }

  if (link.cartLineId && lines?.some((line) => line?.id === link.cartLineId)) {
    return link.cartLineId;
  }

  if (link.parentVariantId) {
    return lines?.find(
      (line) =>
        line?.id &&
        line.merchandise?.id === link.parentVariantId &&
        isProductGwpParentLineCandidate(line)
    )?.id;
  }

  if (isLegacyProductGwpGiftLink(link)) {
    const eligibleParentLines =
      lines?.filter(
        (line): line is CartLine =>
          !!line?.id && isProductGwpParentLineCandidate(line)
      ) ?? [];

    return eligibleParentLines.length === 1
      ? eligibleParentLines[0]?.id
      : undefined;
  }

  return undefined;
};

export const getProductGwpTotalParentQuantityForVariant = (
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  parentVariantId: string,
  excludeCartLineId?: CartLine["id"]
): number => {
  if (!lines?.length || !parentVariantId) {
    return 0;
  }

  return lines.reduce((sum, line) => {
    if (
      !line?.id ||
      line.id === excludeCartLineId ||
      !isProductGwpParentLineCandidate(line) ||
      line.merchandise?.id !== parentVariantId
    ) {
      return sum;
    }

    const quantity = line.quantity;
    return sum + (typeof quantity === "number" && quantity > 0 ? quantity : 1);
  }, 0);
};

type ProductGwpGiftMatchContext = {
  activeParentLineIds: Set<string>;
  gwpParentLineCount: number;
  parentCartLineId?: CartLine["id"];
  parentVariantId: string;
  parentVariantIdsForLegacy: Set<string>;
};

const getProductGwpGiftMatchContext = (
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  parentVariantId: string,
  parentCartLineId?: CartLine["id"]
): ProductGwpGiftMatchContext => {
  const activeParentLineIds = new Set<string>();
  const parentVariantIdsForLegacy = new Set<string>();
  let gwpParentLineCount = 0;

  for (const line of lines ?? []) {
    if (!line?.id) {
      continue;
    }

    if (
      hasCartLineAttribute(line, STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE)
    ) {
      continue;
    }

    activeParentLineIds.add(line.id);

    if (!isProductGwpParentLineCandidate(line)) {
      continue;
    }

    gwpParentLineCount += 1;

    const variantId = line.merchandise?.id;
    if (typeof variantId === "string") {
      parentVariantIdsForLegacy.add(variantId);
    }
  }

  return {
    activeParentLineIds,
    gwpParentLineCount,
    parentCartLineId,
    parentVariantId,
    parentVariantIdsForLegacy,
  };
};

const getParentLineIdsForVariant = (
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  parentVariantId: string
): Set<string> =>
  new Set(
    (lines ?? []).flatMap((line) => {
      if (
        !line?.id ||
        !isProductGwpParentLineCandidate(line) ||
        line.merchandise?.id !== parentVariantId
      ) {
        return [];
      }

      return [line.id];
    })
  );

const matchesExactParentCartLineId = (
  link: ProductGwpGiftLink,
  context: ProductGwpGiftMatchContext
): boolean =>
  !!context.parentCartLineId && link.cartLineId === context.parentCartLineId;

const matchesLinkedParentVariantId = (
  link: ProductGwpGiftLink,
  context: ProductGwpGiftMatchContext
): boolean => {
  if (link.parentVariantId !== context.parentVariantId) {
    return false;
  }

  if (
    context.parentCartLineId &&
    link.cartLineId &&
    context.activeParentLineIds.has(link.cartLineId) &&
    link.cartLineId !== context.parentCartLineId
  ) {
    return false;
  }

  return true;
};

const matchesParentLineIdForVariantLookup = (
  link: ProductGwpGiftLink,
  context: ProductGwpGiftMatchContext,
  parentLineIdsForVariant: Set<string>
): boolean =>
  !!link.cartLineId &&
  parentLineIdsForVariant.has(link.cartLineId) &&
  !context.parentCartLineId;

const matchesStaleParentCartLineId = (
  link: ProductGwpGiftLink,
  context: ProductGwpGiftMatchContext
): boolean => {
  const hasStaleParentCartLineId =
    !!link.cartLineId && !context.activeParentLineIds.has(link.cartLineId);

  if (!hasStaleParentCartLineId) {
    return false;
  }

  if (
    link.parentVariantId &&
    link.parentVariantId === context.parentVariantId
  ) {
    return true;
  }

  return (
    context.gwpParentLineCount === 1 &&
    !!context.parentCartLineId &&
    !link.parentVariantId
  );
};

const matchesLegacyProductGwpGiftLink = (
  link: ProductGwpGiftLink,
  context: ProductGwpGiftMatchContext,
  parentLineIdsForVariant: Set<string>
): boolean => {
  if (!isLegacyProductGwpGiftLink(link)) {
    return false;
  }

  if (context.parentCartLineId) {
    return (
      parentLineIdsForVariant.size === 1 &&
      parentLineIdsForVariant.has(context.parentCartLineId)
    );
  }

  return (
    context.parentVariantIdsForLegacy.size === 1 &&
    context.parentVariantIdsForLegacy.has(context.parentVariantId)
  );
};

const doesProductGwpGiftLinkMatchContext = (
  link: ProductGwpGiftLink,
  context: ProductGwpGiftMatchContext,
  parentLineIdsForVariant: Set<string>
): boolean =>
  matchesExactParentCartLineId(link, context) ||
  matchesLinkedParentVariantId(link, context) ||
  matchesParentLineIdForVariantLookup(link, context, parentLineIdsForVariant) ||
  matchesStaleParentCartLineId(link, context) ||
  matchesLegacyProductGwpGiftLink(link, context, parentLineIdsForVariant);

const getProductGwpGiftLinesMatchingContext = (
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  context: ProductGwpGiftMatchContext
): CartLine[] => {
  const parentLineIdsForVariant = getParentLineIdsForVariant(
    lines,
    context.parentVariantId
  );

  return (lines ?? []).filter((giftLine): giftLine is CartLine => {
    if (!giftLine?.id) {
      return false;
    }

    const giftAttribute = giftLine.attributes?.find(
      (attribute) =>
        attribute?.key === STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
    );
    const link = parseProductGwpGiftLink(giftAttribute?.value);
    if (!link) {
      return false;
    }

    return doesProductGwpGiftLinkMatchContext(
      link,
      context,
      parentLineIdsForVariant
    );
  });
};

export const sumProductGwpGiftLineQuantities = (
  giftLines: Array<Pick<CartLine, "quantity">>
): number =>
  giftLines.reduce((sum, giftLine) => {
    const quantity = giftLine.quantity;
    return sum + (typeof quantity === "number" && quantity > 0 ? quantity : 1);
  }, 0);

export const getProductGwpGiftLinesForVariant = (
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  parentVariantId: string
): CartLine[] => {
  if (!lines?.length || !parentVariantId) {
    return [];
  }

  return getProductGwpGiftLinesMatchingContext(
    lines,
    getProductGwpGiftMatchContext(lines, parentVariantId)
  );
};

export const getProductGwpGiftLinesForParent = (
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  parentCartLineId: CartLine["id"]
): CartLine[] => {
  if (!lines?.length) {
    return [];
  }

  const parentLine = lines.find((line) => line?.id === parentCartLineId);
  const parentVariantId = parentLine?.merchandise?.id;

  if (isProductGwpParentLineCandidate(parentLine) && parentVariantId) {
    return getProductGwpGiftLinesMatchingContext(
      lines,
      getProductGwpGiftMatchContext(lines, parentVariantId, parentCartLineId)
    );
  }

  return (lines ?? []).filter((giftLine): giftLine is CartLine => {
    if (!giftLine?.id) {
      return false;
    }

    const giftAttribute = giftLine.attributes?.find(
      (attribute) =>
        attribute?.key === STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
    );
    const link = parseProductGwpGiftLink(giftAttribute?.value);

    return link?.cartLineId === parentCartLineId;
  });
};

export const isProductGwpGiftLine = (
  line: CartLineLike | null | undefined
): boolean => {
  if (!line?.id) {
    return false;
  }

  const giftAttribute = line.attributes?.find(
    (attribute) =>
      attribute?.key === STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
  );

  return !!parseProductGwpGiftLink(giftAttribute?.value);
};

export type ProductGwpExcessGiftAdjustment = {
  line: CartLine;
  quantity: number;
};

export const getProductGwpExcessGiftAdjustments = (
  cartLineId: CartLine["id"],
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  newQuantityForLine: number
): ProductGwpExcessGiftAdjustment[] => {
  const parentLine = lines?.find((line) => line?.id === cartLineId);
  const parentVariantId = parentLine?.merchandise?.id;
  const isGwpParent = isProductGwpParentLineCandidate(parentLine);
  const linkedToParent = getProductGwpGiftLinesForParent(lines, cartLineId);

  if (!parentVariantId) {
    return [];
  }

  if (!isGwpParent) {
    if (!linkedToParent.length) {
      return [];
    }

    if (newQuantityForLine <= 0) {
      return linkedToParent.map((giftLine) => ({
        line: giftLine,
        quantity: 0,
      }));
    }

    if (sumProductGwpGiftLineQuantities(linkedToParent) <= newQuantityForLine) {
      return [];
    }

    return allocateProductGwpGiftQuantityReductions(
      linkedToParent,
      sumProductGwpGiftLineQuantities(linkedToParent) - newQuantityForLine
    );
  }

  const otherParentQty = getProductGwpTotalParentQuantityForVariant(
    lines,
    parentVariantId,
    cartLineId
  );
  const allGiftsForVariant = getProductGwpGiftLinesForVariant(
    lines,
    parentVariantId
  );

  if (newQuantityForLine <= 0 && otherParentQty === 0) {
    return allGiftsForVariant.map((giftLine) => ({
      line: giftLine,
      quantity: 0,
    }));
  }

  const newTotalParentQty = otherParentQty + Math.max(0, newQuantityForLine);
  const excessUnits = Math.max(
    0,
    sumProductGwpGiftLineQuantities(allGiftsForVariant) - newTotalParentQty
  );

  if (excessUnits === 0) {
    return [];
  }

  const reductionPool =
    linkedToParent.length > 0 ? linkedToParent : allGiftsForVariant;

  return allocateProductGwpGiftQuantityReductions(
    reductionPool,
    Math.min(excessUnits, sumProductGwpGiftLineQuantities(reductionPool))
  );
};

const allocateProductGwpGiftQuantityReductions = (
  giftLines: CartLine[],
  unitsToRemove: number
): ProductGwpExcessGiftAdjustment[] => {
  if (unitsToRemove <= 0) {
    return [];
  }

  const adjustments: ProductGwpExcessGiftAdjustment[] = [];
  let remainingUnits = unitsToRemove;

  for (const giftLine of [...giftLines].reverse()) {
    if (remainingUnits <= 0) {
      break;
    }

    const currentQuantity =
      typeof giftLine.quantity === "number" && giftLine.quantity > 0
        ? giftLine.quantity
        : 1;
    const quantityToRemove = Math.min(currentQuantity, remainingUnits);
    adjustments.push({
      line: giftLine,
      quantity: currentQuantity - quantityToRemove,
    });
    remainingUnits -= quantityToRemove;
  }

  return adjustments;
};

export const getProductGwpExcessGiftLinesToRemove = (
  cartLineId: CartLine["id"],
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  newQuantityForLine: number
): CartLine[] =>
  getProductGwpExcessGiftAdjustments(cartLineId, lines, newQuantityForLine)
    .filter((adjustment) => adjustment.quantity === 0)
    .map((adjustment) => adjustment.line);

export const getRelatedCartLinesToRemove = (
  cartLineId: CartLine["id"],
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined
): CartLine["id"][] => {
  const giftLines = getProductGwpExcessGiftLinesToRemove(cartLineId, lines, 0);

  return [
    cartLineId,
    ...giftLines
      .map((giftLine) => giftLine.id)
      .filter((id): id is CartLine["id"] => !!id),
  ];
};
